/**
 * Vehicle Repairs Page
 * 
 * Premium modern UI for managing repair requests and history for vehicles
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import api from '../../../lib/api';
import { 
  Plus, 
  Search, 
  Wrench, 
  Calendar, 
  DollarSign, 
  X, 
  Activity, 
  ShieldCheck, 
  ChevronRight,
  Clock,
  Filter,
  ArrowLeft,
  Truck,
  LayoutGrid
} from 'lucide-react';

export default function Repairs() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  
  const [repairs, setRepairs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);

  // Load repairs and vehicles
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load vehicles for dropdown
      const vehiclesData = await api.get('/api/vehicles');
      setVehicles(vehiclesData);
      
      // Load vehicle repairs
      // TODO: Update when vehicle repairs API endpoint is ready
      // For now, try the endpoint or use empty array
      try {
        const repairsData = await api.get('/api/vehicle-repairs');
        setRepairs(repairsData);
      } catch (err) {
        console.log('Vehicle repairs endpoint not available yet');
        setRepairs([]);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter repairs
  const filteredRepairs = repairs.filter(repair => {
    const vehicle = vehicles.find(v => v.id === repair.vehicle_id);
    const vehicleName = vehicle?.vehicle_name || `${vehicle?.year} ${vehicle?.make} ${vehicle?.model}` || 'Unknown Vehicle';
    const roNumber = repair.shop_ro_number || repair.ro_number || '';
    const description = repair.repair_description || repair.customer_concern || repair.description || '';
    
    const matchesSearch = 
      vehicleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      roNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const status = repair.shop_status || repair.status || 'Submitted';
    const matchesFilter = filterStatus === 'All' || status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const inProgressCount = repairs.filter(r => {
    const status = r.shop_status || r.status || '';
    return status === 'In Progress' || status === 'In Repair';
  }).length;
  
  const completedCount = repairs.filter(r => {
    const status = r.shop_status || r.status || '';
    return status === 'Complete' || status === 'Finalized';
  }).length;
  
  const totalCost = repairs.reduce((acc, curr) => {
    return acc + (parseFloat(curr.final_cost || curr.estimated_cost || 0));
  }, 0);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-400 font-medium animate-pulse">Loading service history...</p>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-slate-950 text-slate-100 flex flex-col overflow-hidden relative">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-indigo-600/10 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 blur-[150px] rounded-full"></div>
      </div>

      <header className="px-12 py-10 flex flex-col md:flex-row md:items-end justify-between gap-6 z-10 shrink-0">
        <div className="flex items-start gap-6">
          <button 
            onClick={() => navigate('/vehicles')}
            className="mt-2 p-2.5 hover:bg-slate-800/50 rounded-xl transition-colors border border-slate-800 text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-5xl font-black tracking-tighter text-white">Repairs</h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
            <input 
              type="text" 
              placeholder="Search history..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-2xl pl-11 pr-6 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all w-64 md:w-80 text-white placeholder-slate-600"
            />
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3.5 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-indigo-600/20 active:scale-95"
          >
            <Plus className="w-4 h-4" />
            New Request
          </button>
        </div>
      </header>

      {/* Quick Stats Overlay */}
      <div className="px-12 pb-8 flex gap-8 z-10 overflow-x-auto no-scrollbar shrink-0">
        <QuickStat icon={<Clock className="w-4 h-4 text-amber-400" />} label="In Progress" value={inProgressCount} />
        <QuickStat icon={<ShieldCheck className="w-4 h-4 text-emerald-400" />} label="Completed" value={completedCount} />
        <QuickStat icon={<DollarSign className="w-4 h-4 text-blue-400" />} label="Total Spend" value={`$${totalCost.toLocaleString()}`} />
      </div>

      {/* Filters Hub */}
      <div className="px-12 pb-6 flex gap-2 z-10 shrink-0">
        {['All', 'Submitted', 'In Progress', 'Waiting on Parts', 'Complete'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-6 py-2 rounded-full text-xs font-bold transition-all border ${
              filterStatus === status 
                ? 'bg-white text-slate-950 border-white shadow-lg' 
                : 'bg-slate-900/50 text-slate-400 border-slate-800 hover:border-slate-600'
            }`}
          >
            {status}
          </button>
        ))}
      </div>

      <main className="flex-1 px-12 pb-12 overflow-y-auto no-scrollbar z-10 min-h-0">
        <div className="space-y-4">
          {filteredRepairs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 bg-slate-900/20 border border-dashed border-slate-800 rounded-[40px]">
              <Wrench className="w-16 h-16 text-slate-700 mb-4" />
              <h3 className="text-xl font-bold text-slate-400">No records found</h3>
              <p className="text-slate-600 mt-1">
                {searchTerm || filterStatus !== 'All' 
                  ? 'Refine your search or filters'
                  : 'Get started by creating your first repair request'
                }
              </p>
              {!searchTerm && filterStatus === 'All' && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="mt-6 flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3.5 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-indigo-600/20"
                >
                  <Plus className="w-4 h-4" />
                  New Repair Request
                </button>
              )}
            </div>
          ) : (
            filteredRepairs.map((repair) => {
              const vehicle = vehicles.find(v => v.id === repair.vehicle_id);
              const vehicleName = vehicle?.vehicle_name || `${vehicle?.year} ${vehicle?.make} ${vehicle?.model}` || 'Unknown Vehicle';
              const status = repair.shop_status || repair.status || 'Submitted';
              const roNumber = repair.shop_ro_number || repair.ro_number || 'N/A';
              const category = repair.repair_category || repair.category || 'General';
              const description = repair.repair_description || repair.customer_concern || repair.description || 'No description';
              const cost = parseFloat(repair.final_cost || repair.estimated_cost || 0);
              const date = repair.date_submitted || repair.created_at || new Date().toISOString();
              const formattedDate = new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              
              return (
                <div 
                  key={repair.id}
                  className="group relative bg-slate-900/40 backdrop-blur-xl border border-slate-800 p-6 rounded-[32px] hover:border-indigo-500/50 hover:bg-slate-900/60 transition-all duration-500 flex items-center gap-8"
                >
                  {/* Visual Indicator */}
                  <div className="flex-shrink-0 w-24 h-24 bg-slate-950 rounded-2xl flex items-center justify-center border border-slate-800 relative overflow-hidden">
                    <Truck className="w-10 h-10 text-slate-700 opacity-50" />
                    <div className={`absolute bottom-0 left-0 w-full h-1 ${
                      status === 'Complete' || status === 'Finalized' ? 'bg-emerald-500' :
                      status === 'In Progress' || status === 'In Repair' ? 'bg-blue-500' :
                      'bg-amber-500'
                    }`}></div>
                  </div>

                  <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                    <div className="col-span-1">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Vehicle Asset</p>
                      <h3 className="text-lg font-bold text-white group-hover:text-indigo-400 transition-colors">{vehicleName}</h3>
                      <p className="text-xs font-mono text-slate-500">{roNumber}</p>
                    </div>

                    <div className="col-span-1">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Service Details</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded-md bg-slate-800 text-[10px] font-bold text-slate-400 border border-slate-700">{category}</span>
                        <span className="text-sm font-medium text-slate-300 truncate max-w-[150px]" title={description}>{description}</span>
                      </div>
                    </div>

                    <div className="col-span-1">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Status & Timeline</p>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          status === 'Complete' || status === 'Finalized' ? 'bg-emerald-500' :
                          status === 'In Progress' || status === 'In Repair' ? 'bg-blue-500' :
                          'bg-amber-500'
                        }`}></div>
                        <span className="text-sm font-bold text-slate-100">{status}</span>
                        <span className="text-xs text-slate-500 font-mono ml-auto">{formattedDate}</span>
                      </div>
                    </div>

                    <div className="col-span-1 flex items-center justify-end gap-6">
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Cost</p>
                        <span className="text-xl font-black text-white">${cost > 0 ? cost.toLocaleString() : 'N/A'}</span>
                      </div>
                      <button 
                        onClick={() => navigate(`/vehicles/${repair.vehicle_id}`)}
                        className="p-3 rounded-2xl bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-all opacity-0 group-hover:opacity-100"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>


      {/* Add Request Modal */}
      {showAddModal && (
        <AddRepairModal 
          vehicles={vehicles}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            loadData();
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
}

// Quick Stat Component
const QuickStat = ({ icon, label, value }) => (
  <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 px-6 py-4 rounded-2xl flex items-center gap-4 min-w-[200px]">
    <div className="p-2.5 rounded-xl bg-slate-800 border border-slate-700">
      {icon}
    </div>
    <div>
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-xl font-black text-white">{value}</p>
    </div>
  </div>
);

// Add Repair Modal Component
function AddRepairModal({ vehicles, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    customer_concern: '',
    repair_category: 'General',
    priority: 'Medium',
    request_type: 'In Shop'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicle_id || !formData.customer_concern) {
      alert('Please select a vehicle and provide a description');
      return;
    }

    setSaving(true);
    try {
      // TODO: Update when vehicle repairs API endpoint is ready
      // For now, this will fail gracefully
      await api.post('/api/vehicle-repairs', {
        vehicle_id: formData.vehicle_id,
        customer_concern: formData.customer_concern,
        repair_category: formData.repair_category,
        priority: formData.priority,
        request_type: formData.request_type,
        shop_status: 'Submitted'
      });
      
      onSuccess();
    } catch (err) {
      console.error('Failed to create repair:', err);
      alert(err.message || 'Failed to create repair request. The API endpoint may not be ready yet.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md">
      <div className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-[40px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="px-8 py-8 flex justify-between items-start border-b border-slate-800">
          <div>
            <h2 className="text-2xl font-black tracking-tight text-white">Initiate Service Request</h2>
            <p className="text-slate-500 text-sm font-medium mt-1">Schedule maintenance or report a performance anomaly.</p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-800 rounded-full text-slate-500 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-8 space-y-6">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Select Vehicle</label>
              <select 
                value={formData.vehicle_id}
                onChange={(e) => setFormData({...formData, vehicle_id: e.target.value})}
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/50 appearance-none"
                required
              >
                <option value="">Choose a vehicle...</option>
                {vehicles.map(vehicle => {
                  const displayName = vehicle.vehicle_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
                  return (
                    <option key={vehicle.id} value={vehicle.id}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Service Description</label>
              <textarea 
                rows={3}
                placeholder="Detail the required maintenance..."
                value={formData.customer_concern}
                onChange={(e) => setFormData({...formData, customer_concern: e.target.value})}
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white font-medium placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Category</label>
                <select 
                  value={formData.repair_category}
                  onChange={(e) => setFormData({...formData, repair_category: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                  <option>General</option>
                  <option>Mechanical</option>
                  <option>Electronics</option>
                  <option>Maintenance</option>
                  <option>Tires</option>
                  <option>Engine / Codes</option>
                  <option>Transmission</option>
                  <option>Brakes</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Priority</label>
                <select 
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 bg-slate-950/50 border-t border-slate-800 flex justify-end gap-3">
            <button 
              type="button"
              onClick={onClose} 
              className="px-8 py-3 rounded-2xl text-sm font-bold text-slate-400 hover:text-white transition-colors"
            >
              Discard
            </button>
            <button 
              type="submit"
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-10 py-3 rounded-2xl text-sm font-bold transition-all shadow-xl shadow-indigo-600/20"
            >
              {saving ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
